"use client";

import {
  AtSign,
  CreditCard,
  User,
  Database,
  FileSearch,
  Paintbrush,
  MoreHorizontal,
  ArrowRight,
  Check,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import type React from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import Image from "next/image";

export default function GridList07() {
  return (
    <section className="pb-20 pt-20 md:pb-32 md:pt-32 container mx-auto">
      <div className="text-center space-y-4 pb-16 mx-auto max-w-4xl">
        <Badge>FEATURES</Badge>
        <h2 className="mx-auto mt-4 text-3xl font-bold sm:text-5xl tracking-tight">
          Supercharge your app instantly
        </h2>
        <p className="text-xl text-muted-foreground pt-1">
          Login users, process payments and send emails at lightspeed. Spend
          your time building your startup, not integrating APIs. ShipFast
          provides you with the boilerplate code you need to launch, FAST.
        </p>
      </div>
      <div className="w-full flex justify-center align-center">
        <Tabs defaultValue="emails" className="max-w-5xl mb-16">
          <TabsList className="grid grid-cols-3 sm:grid-cols-7 h-auto bg-transparent gap-4 overflow-x-auto mx-auto">
            <TabTrigger
              value="emails"
              icon={<AtSign className="w-6 h-6" />}
              label="Emails"
            />
            <TabTrigger
              value="payments"
              icon={<CreditCard className="w-6 h-6" />}
              label="Payments"
            />
            <TabTrigger
              value="login"
              icon={<User className="w-6 h-6" />}
              label="Login"
            />
            <TabTrigger
              value="database"
              icon={<Database className="w-6 h-6" />}
              label="Database"
            />
            <TabTrigger
              value="seo"
              icon={<FileSearch className="w-6 h-6" />}
              label="SEO"
            />
            <TabTrigger
              value="style"
              icon={<Paintbrush className="w-6 h-6" />}
              label="Style"
            />
            <TabTrigger
              value="more"
              icon={<MoreHorizontal className="w-6 h-6" />}
              label="More"
            />
          </TabsList>

          <TabsContent
            value="emails"
            className="mt-14 animate-in fade-in-50 duration-300"
          >
            <FeatureContent
              title="Email Infrastructure, Ready to Go"
              description="Send transactional emails without the headache. Our pre-configured email system saves you hours of setup time and ensures your messages land in inboxes, not spam folders."
              image="/dummy-image.svg"
              features={[
                "Send transactional emails with one line of code",
                "DNS setup to avoid spam folder (DKIM, DMARC, SPF in subdomain)",
                "Webhook to receive & forward emails",
                "Time saved: 3 hours",
                "Headaches: 0",
              ]}
              highlightIndices={[3, 4]}
            />
          </TabsContent>

          <TabsContent
            value="payments"
            className="mt-14 animate-in fade-in-50 duration-300"
          >
            <FeatureContent
              title="Payment Processing, Simplified"
              description="Accept payments instantly with our pre-built Stripe integration. Start making money from day one without wrestling with payment APIs."
              image="/dummy-image.svg"
              features={[
                "One-click Stripe integration",
                "Subscription management built-in",
                "Secure payment processing",
                "Time saved: 5 hours",
                "Revenue potential: Unlimited",
              ]}
              highlightIndices={[3, 4]}
            />
          </TabsContent>

          <TabsContent
            value="login"
            className="mt-14 animate-in fade-in-50 duration-300"
          >
            <FeatureContent
              title="Authentication, Done Right"
              description="Secure user authentication without the complexity. Our system handles logins, registrations, and profile management so you can focus on your core features."
              image="/dummy-image.svg"
              features={[
                "Social login (Google, GitHub, etc.)",
                "Email/password authentication",
                "Password reset flows",
                "Time saved: 8 hours",
                "Security headaches avoided: Countless",
              ]}
              highlightIndices={[3, 4]}
            />
          </TabsContent>

          <TabsContent
            value="database"
            className="mt-14 animate-in fade-in-50 duration-300"
          >
            <FeatureContent
              title="Database That Scales With You"
              description="Start with a powerful database setup that grows as you do. Our pre-configured database solution handles everything from simple data storage to complex queries."
              image="/dummy-image.svg"
              features={[
                "Serverless database configuration",
                "Automatic backups and migrations",
                "Type-safe database queries",
                "Time saved: 6 hours",
                "Scaling worries: None",
              ]}
              highlightIndices={[3, 4]}
            />
          </TabsContent>

          <TabsContent
            value="seo"
            className="mt-14 animate-in fade-in-50 duration-300"
          >
            <FeatureContent
              title="SEO Optimization Built-In"
              description="Get discovered faster with our SEO-optimized templates. We've implemented all the best practices so your app ranks higher from day one."
              image="/dummy-image.svg"
              features={[
                "Meta tags pre-configured",
                "Sitemap generation",
                "Structured data support",
                "Time saved: 4 hours",
                "Organic traffic potential: Maximized",
              ]}
              highlightIndices={[3, 4]}
            />
          </TabsContent>

          <TabsContent
            value="style"
            className="mt-14 animate-in fade-in-50 duration-300"
          >
            <FeatureContent
              title="Design System That Impresses"
              description="Look professional from day one with our customizable design system. Beautiful components that work together seamlessly."
              image="/dummy-image.svg"
              features={[
                "Responsive UI components",
                "Dark/light mode support",
                "Customizable theme",
                "Time saved: 10 hours",
                "Design compliments: Countless",
              ]}
              highlightIndices={[3, 4]}
            />
          </TabsContent>

          <TabsContent
            value="more"
            className="mt-14 animate-in fade-in-50 duration-300"
          >
            <FeatureContent
              title="And So Much More"
              description="Discover all the additional features that make ShipFast the ultimate starter kit for your next big idea."
              image="/dummy-image.svg"
              features={[
                "Analytics integration",
                "Internationalization support",
                "Performance optimization",
                "Time saved: 20+ hours",
                "Launch timeline: Accelerated by weeks",
              ]}
              highlightIndices={[3, 4]}
            />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
}

interface TabTriggerProps {
  value: string;
  icon: React.ReactNode;
  label: string;
}

const TabTrigger: React.FC<TabTriggerProps> = ({ value, icon, label }) => {
  return (
    <TabsTrigger
      value={value}
      className="flex flex-col items-center gap-2 data-[state=active]:bg-primary/10 data-[state=active]:text-primary 
        bg-muted hover:bg-muted/80 p-4 h-auto border-0 transition-all duration-200 ease-in-out"
    >
      <div className="data-[state=active]:text-primary text-muted-foreground transition-colors duration-200">
        {icon}
      </div>
      <span className="text-sm transition-colors duration-200">{label}</span>
    </TabsTrigger>
  );
};

interface FeatureContentProps {
  title: string;
  description: string;
  image: string;
  features: string[];
  highlightIndices?: number[];
}

const FeatureContent: React.FC<FeatureContentProps> = ({
  title,
  description,
  image,
  features,
  highlightIndices = [],
}) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
      {/* Image Section */}
      <div className="relative">
        <div className="absolute inset-0 bg-primary rounded-2xl transform rotate-3"></div>
        <div className="relative overflow-hidden rounded-xl border-4 border-black transform -rotate-2 transition-transform hover:rotate-0 duration-300">
          <Image
            src={image || "/placeholder.svg"}
            alt={title}
            width={800}
            height={600}
            className="w-full h-auto object-cover"
          />
        </div>
        <div className="absolute -bottom-4 -right-4 bg-black text-white font-bold py-2 px-4 rounded-lg transform rotate-3 text-sm uppercase tracking-wider">
          Included
        </div>
      </div>

      {/* Content Section */}
      <div className="space-y-6">
        <div className="space-y-4">
          <h3 className="text-2xl font-bold track">{title}</h3>
          <p className="text-lg text-muted-foreground">{description}</p>
        </div>

        <div className="space-y-4 my-8">
          {features.map((feature, index) => (
            <Feature
              key={index}
              text={feature}
              highlight={highlightIndices.includes(index)}
            />
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-4 pt-4">
          <Button className="font-bold !px-8 py-6 text-lg transition-all hover:translate-y-[-2px] hover:shadow-lg">
            Get Started
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
          <Button
            variant="outline"
            className="font-bold !px-8 py-6 rounded-xl text-lg transition-all hover:translate-y-[-2px]"
          >
            Learn More
          </Button>
        </div>
      </div>
    </div>
  );
};

interface FeatureProps {
  text: string;
  highlight?: boolean;
}

const Feature: React.FC<FeatureProps> = ({ text, highlight = false }) => {
  return (
    <div className="flex items-center gap-3 group">
      <div
        className={`flex items-center justify-center rounded-full p-1 ${
          highlight ? "bg-emerald-500" : "bg-muted"
        } transition-all duration-300 ease-in-out group-hover:scale-110`}
      >
        <Check
          className={`w-4 h-4 ${highlight ? "text-white" : "text-foreground"}`}
        />
      </div>
      <span
        className={`${
          highlight ? "text-emerald-500 font-bold" : ""
        } group-hover:translate-x-1 transition-transform duration-300 ease-in-out`}
      >
        {text}
      </span>
    </div>
  );
};
